import { inject, Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { produce } from 'immer';
import {
  catchError,
  concatMap,
  delay,
  distinctUntilChanged,
  filter,
  first,
  map,
  Observable,
  of,
  pairwise,
  startWith,
  switchMap,
  tap,
} from 'rxjs';

import {
  PageContextFacade,
  runtimeActions,
  runtimeSelectors,
} from '@blaze-runtime/engine-data-access';
import {
  CanvasElement,
  getNamespaceId,
  getRootElementId,
  PartialPropsOf,
} from '@blaze-runtime/engine-domain';
import { fromRouter } from '@blaze-shared/app-state-data-access';
import {
  CanvasElementDataProperties,
  DataBindingSourceInfo,
  DataBindingSourceType,
  FormOrigin,
  getActiveBinding,
  RuntimePage,
  WizardFormDraft,
} from '@blaze-shared/elements-domain';
import { syncSelect } from '@blaze-shared/platform-data-access';
import { PageContext } from '@blaze-shared/projects-domain';
import { DatasetRowsService, SchemaService } from '@blaze-shared/schema-data-access';
import { BlazeSchema, DatasetRow, type RuntimeEventType } from '@blaze-shared/schema-domain';
import {
  isDeepEqual,
  isEmptyish,
  isNullish,
  isNullishOrEmptyObject,
} from '@blaze-shared/shared-util';
import { isGetNode, WorkflowPhase } from '@blaze-shared/workflow-domain';

import { getElementTypeById } from '../../../../+state/elements/elements.selectors';
import { ElementDefinitionsService } from '../../../../services/element-definitions.service';
import { ElementStoreViewService } from '../../../../services/element-store-view.service';
import { RowGenerationService } from '../../../../services/row-generation.service';
import { findParentFormGroupLike } from '../../../../utils/layout/relationship';
import { findWorkflowSourceNode } from '../../../element-model/data-binding';
import { hasDataProps } from '../../../element-model/element-props-guards';
import {
  isEditableForm,
  isElementType,
  isVariantOfFormContainer,
} from '../../../element-model/element-type-guards';
import { DataProviderQuery, InputDataPackage } from '../../io-data.models';

import { DataSourceProvider } from './data-source.data-provider';
import { fillCustomFields, getFormCustomFields } from './utils/form-custom-fields.utils';
import { WizardDraftService } from './utils/wizard-draft.service';

interface InitialData {
  currentPage: RuntimePage | undefined;
  /**
   * Says whether the element is placed on a dialog-like (dialog, drawer)
   */
  isOnDialogLike: boolean;
}

interface PageContextWithSource extends Partial<PageContext> {
  ctxSource?: 'pageContext' | 'empty-row' | 'workflow' | 'draft';
}

interface FormDataInputDto {
  element: CanvasElement;
  formContainerId: string;
  binding: CanvasElementDataProperties | undefined;
  ctx: PageContextWithSource | undefined;
  wizardDraft: WizardFormDraft | undefined;
  customFields: string[] | undefined;
  defaultSelfOutput: RuntimeEventType;
}

interface FormDataContext {
  ctx: PageContextWithSource | undefined;
  customFields: string[] | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class SelfDataProvider implements DataSourceProvider {
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly elementStoreView = inject(ElementStoreViewService);
  private readonly pageContextFacade = inject(PageContextFacade);
  private readonly rowGenerationService = inject(RowGenerationService);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);
  private readonly wizardDraftService = inject(WizardDraftService);
  readonly sourceType: DataBindingSourceType = 'self';

  getDataStream({ source, element, binding }: DataProviderQuery): Observable<InputDataPackage> {
    const formContainerId = source.id;
    const defaultSelfOutput = this.elementDefinitionsService.getDefaultOutputEventType(
      element.type,
    );

    if (!defaultSelfOutput) {
      throw new Error('Missing default output');
    }

    // The data source id is an element's id
    const namespace = getNamespaceId(formContainerId);
    const pageContext$ = this.pageContextFacade.getPageContextForPage(namespace);
    const { currentPage, isOnDialogLike } = this.getInitialData(namespace);

    // Get data from page context or dataset
    return pageContext$.pipe(
      distinctUntilChanged(isDeepEqual),
      switchMap((pageContext) => {
        return this.getFormPageContext(element, pageContext, currentPage, isOnDialogLike);
      }),
      // Append Custom Form Fields & Wizard Draft data if applicable
      concatMap((ctx) => {
        return this.getDraftForWizard(element).pipe(
          map((wizardDraft) => ({
            ctx,
            wizardDraft,
            customFields: getFormCustomFieldsIfEditable(element),
          })),
        );
      }),
      // Initialize form data
      concatMap(({ ctx, wizardDraft, customFields }) => {
        // This prepares the data and calls some side effects updating the state
        return this.initializeFormData({
          element,
          formContainerId,
          binding,
          ctx,
          wizardDraft,
          customFields,
          defaultSelfOutput,
        });
      }),
      // This delay in CRUCIAL to avoid infinite loop. The code above triggers some side effect updates
      // which need to finish propagating before we subscribe to UI output.
      delay(0),

      // Subscribe to the form container output
      concatMap(({ ctx, customFields }) => {
        return this.getFormOutputDataAndSchema(
          formContainerId,
          element,
          binding?.dataSource,
          ctx,
          customFields,
          defaultSelfOutput,
        );
      }),

      // Prepare the result
      // Due to the fact that the form's schema here can originate from the dataset, workflow
      // and the form container itself - the schema might slightly change (id, workflowId, etc)
      // we just make sure that the data and columns are different.
      // In the future, once the logic is more clear, we can improve this.
      distinctUntilChanged(
        (prev, next) =>
          isDeepEqual(prev.data, next.data) &&
          isDeepEqual(prev.schema?.columns, next.schema?.columns),
      ),
      map(({ data, schema }) => {
        return {
          sourceType: this.sourceType,
          data,
          schema,
        };
      }),
    );
  }

  /**
   * Connects to the output of the form container
   * and keeps the data and schema in sync
   */
  private getFormOutputDataAndSchema(
    formContainerId: string,
    element: CanvasElement,
    formDataSource: DataBindingSourceInfo | undefined,
    ctx: PageContextWithSource | undefined,
    customFields: string[] | undefined,
    defaultSelfOutput: RuntimeEventType,
  ): Observable<{
    data: any;
    schema: BlazeSchema | undefined;
  }> {
    // connect to the default output of the form-like element
    return this.store
      .select(runtimeSelectors.selectOutputUiWithSchemaById(formContainerId, defaultSelfOutput))
      .pipe(
        // Skip if the output was triggered by formula refresh
        filter((payload) => !payload.output?.updateSource?.startsWith('formulas-refresh')),
        distinctUntilChanged(isDeepEqual),
        startWith(undefined), // for pairwise
        map((payload, index) => {
          const { data } = payload?.output ?? {};
          let { schema } = payload ?? {};

          const emptyRow =
            ctx?.ctxSource === 'workflow'
              ? ctx.data
              : this.rowGenerationService.getNewRow(formDataSource, { customFields });

          // If schema is missing - try to get it from the form data source for the first time output gets emitted
          // index = 1 is the first emissiong (due to the `startWith` above used for pairwise)
          if (!schema?.columns.length && formDataSource && index === 1) {
            schema = this.getSchemaForFormDataSource(formDataSource, schema, element, ctx);
          }

          return { data, schema, emptyRow };
        }),
        pairwise(),
        map(([{ emptyRow: previousEmptyRow }, { data, schema, emptyRow }]) => {
          data = data ?? ctx?.data ?? {};
          data = updateFormIfEmptyRowChanged(
            element,
            data,
            previousEmptyRow ?? {},
            emptyRow ?? {},
            customFields,
          );

          return { data, schema };
        }),
      );
  }

  /**
   * @param formDataSource
   * @param schema previous schema (pass through if source type doesn't match)
   * @param element element for which the binding is initialized
   * @param ctx page context
   * @returns schema for the form data source
   */
  private getSchemaForFormDataSource(
    formDataSource: DataBindingSourceInfo,
    schema: BlazeSchema | undefined,
    element: CanvasElement,
    ctx: PageContextWithSource | undefined,
  ): BlazeSchema | undefined {
    if (formDataSource.sourceType === 'dataset' || formDataSource.sourceType === 'formGroup') {
      return this.schemaService.getSchema(formDataSource.id);
    }

    if (formDataSource.sourceType === 'context') {
      schema = syncSelect(
        this.store,
        runtimeSelectors.selectWorkflowsSchemaById(formDataSource.id),
      );
      if (!schema && ctx?.schema) {
        schema = ctx.schema;
      }
      if (!schema) {
        console.warn(
          `Missing schema for form ${element.id}. Schema for this form originates on another page. Go there and navigate to this page via an action.`,
        );
      }
      return schema;
    }

    // Otherwise pass through the original schema
    return schema;
  }

  private initializeFormData({
    element,
    formContainerId,
    binding,
    ctx,
    wizardDraft,
    customFields,
    defaultSelfOutput,
  }: FormDataInputDto): Observable<FormDataContext> {
    // Prepare context data
    const emptyRow = this.rowGenerationService.getNewRow(binding?.dataSource, { customFields });

    if (wizardDraft) {
      // From Wizard draft
      ctx ??= {};
      ctx.data = wizardDraft;
      ctx.ctxSource = 'draft';
    } else if (
      // Populate empty(ish) row for insert forms
      isEditableForm(element) &&
      element.props.content.action === 'insert' &&
      (!ctx?.data || isEmptyish(ctx?.data) || ctx.isEmptyishRow)
    ) {
      ctx ??= {};

      // Used for Workflows & Form Groups as source
      const bindingToCheck$ = this.getBindingToCheck(binding, element);
      if (bindingToCheck$) {
        return bindingToCheck$.pipe(
          concatMap((bindingToCheck) => this.getBindingData(bindingToCheck, ctx, customFields)),
        );
      }

      // Otherwise - use the empty row data
      ctx.data = ctx.isEmptyishRow ? ctx.data : emptyRow;
      ctx.ctxSource = 'empty-row';
    }

    // If data missing - load sample data
    if (!ctx?.data) {
      // Load sample data just in editor for Details View when no specific data row provided (BL-7411)
      const dataSource = binding?.dataSource;

      const shouldLoadSampleData =
        this.isInEditor() &&
        isElementType(element, 'formDetails') &&
        !ctx?.pageDataRowId &&
        !!dataSource;

      if (!shouldLoadSampleData) {
        return of({ ctx: undefined, customFields });
      }

      return this.populateWithSampleData(dataSource, formContainerId, defaultSelfOutput);
    }

    // container actions don't have an id and their data should be picked up from pageContext
    const data = prepareDataForForm(ctx, element, emptyRow);

    this.store.dispatch(
      runtimeActions.updateRuntimeUiOutput({
        elementId: formContainerId,
        eventType: defaultSelfOutput,
        data,
        updateSource: 'provider',
      }),
    );

    return of({ ctx: { schema: ctx.schema }, customFields });
  }

  private populateWithSampleData(
    dataSource: DataBindingSourceInfo,
    formContainerId: string,
    defaultSelfOutput: RuntimeEventType,
  ): Observable<FormDataContext> {
    return this.loadSampleData(dataSource).pipe(
      map((data) => {
        const ctx: PageContextWithSource = { data };
        return { ctx, customFields: undefined };
      }),
      tap((payload) => {
        const { data } = payload.ctx;
        this.store.dispatch(
          runtimeActions.updateRuntimeUiOutput({
            elementId: formContainerId,
            eventType: defaultSelfOutput,
            data,
            updateSource: 'provider',
          }),
        );
      }),
    );
  }

  /**
   * Prepopulate the fields based on the source query in workflow
   */
  private getBindingData(
    bindingToCheck: CanvasElementDataProperties | undefined,
    ctx: PageContextWithSource | undefined,
    customFields: string[] | undefined,
  ): Observable<FormDataContext> {
    if (!bindingToCheck?.dataSource?.id) {
      return of({ ctx, customFields });
    }

    const dataSource = bindingToCheck.dataSource;

    return this.store
      .select(runtimeSelectors.selectWorkflowsSchemaWithOutputAndStatusById(dataSource.id))
      .pipe(
        filter(
          ({ output, schema, status }) =>
            output !== undefined && !schema?.isComputed && status?.phase !== WorkflowPhase.Running,
        ),
        concatLatestFrom(() =>
          this.store.select(runtimeSelectors.selectWorkflowById(dataSource.id)),
        ),
        map(([{ output, schema }, workflow]) => ({ output, schema, workflow })),
        distinctUntilChanged(isDeepEqual),
        map(({ workflow }) => {
          const srcNode = workflow ? findWorkflowSourceNode(workflow) : undefined;

          if (isGetNode(srcNode)) {
            ctx ??= {};
            ctx.data = this.rowGenerationService.getNewRow(dataSource, {
              prefillData: !!(srcNode.query || srcNode.queryConditions.length),
              customFields,
            });
            ctx.ctxSource = 'workflow';
          }

          return { ctx, customFields };
        }),
      );
  }

  private getBindingToCheck(
    binding: CanvasElementDataProperties | undefined,
    element: CanvasElement,
  ): Observable<CanvasElementDataProperties | undefined> | undefined {
    if (binding?.dataSource?.sourceType === 'context') {
      return of(binding);
    }

    if (binding?.dataSource?.sourceType === 'formGroup') {
      // the wf sub is needed to update new row values from query
      return this.elementStoreView.entities$.pipe(
        map((elements) => {
          const formGroup = findParentFormGroupLike(elements, element.id);

          // If the form group is not found or it's not an add action, return undefined
          if (
            !formGroup ||
            !hasDataProps(formGroup) ||
            (formGroup.props as PartialPropsOf<'formGroup'>).content?.action !== 'add'
          ) {
            return undefined;
          }

          const binding = getActiveBinding(formGroup.props.data, 'value');
          return binding?.dataSource?.sourceType === 'context' ? binding : undefined;
        }),
      );
    }

    return undefined;
  }

  private getDraftForWizard(element: CanvasElement): Observable<WizardFormDraft | undefined> {
    return this.elementStoreView.entities$.pipe(
      first(),
      concatMap((elements) =>
        this.wizardDraftService.getWizardFormDraft(
          element,
          (form) => isEditableForm(form) && form.props.content.origin === 'wizard',
          elements,
        ),
      ),
    );
  }

  private getFormPageContext(
    element: CanvasElement,
    pageContext: PageContext | undefined,
    currentPage: RuntimePage | undefined,
    isOnDialogLike: boolean,
  ): Observable<PageContextWithSource | undefined> {
    // Insert form in a Form Group or Wizard
    const isInsertOnFormGroupLike =
      isEditableForm(element) &&
      (element.props.content?.origin === 'form-group' ||
        element.props.content?.origin === 'wizard') &&
      element.props.content?.action === 'insert';

    const dataRowId = pageContext?.pageDataRowId ?? null;

    // The following needs to happen in order to be able to observe dataset's row
    // - `dataRowId` must be present, so we know which row to observe
    // - should be a Details View, so observed updates don't overwrite the edited data
    // - should be a full page, we don't update the data automatically in a dialog or drawer
    const shouldTryToObserveDataset =
      isInsertOnFormGroupLike || (dataRowId && element.type === 'formDetails' && !isOnDialogLike);

    // If shouldn't observe the dataset, connect to the page context
    if (!shouldTryToObserveDataset) {
      if (pageContext?.formContext) {
        // Apply form context from page context to the form
        this.store.dispatch(
          runtimeActions.updateFormContext({
            formElementId: element.id,
            formContext: pageContext.formContext,
          }),
        );
      }

      return of({ ...pageContext, ctxSource: 'pageContext' as const });
    }

    // Try to identify the dataset id based on the current page
    const mainDatasetId = currentPage?.mainDatasetId;
    if (!mainDatasetId) {
      if (!pageContext) {
        return of(undefined);
      }

      return of({ ...pageContext, ctxSource: 'pageContext' as const });
    }

    // If the ID column cannot be identified, return undefined as there's no way to fetch the data
    const idColumn = this.schemaService.getIdColumnName(mainDatasetId);
    if (!idColumn) {
      return of(undefined);
    }

    // If no context - take data from the dataset
    // If has context - take data from the context and then from the dataset when new data gets emitted (to update the context)
    const context: PageContextWithSource = {
      ...pageContext,
      ctxSource: 'pageContext',
    };

    // If the dataRowId is not set, we don't need to subscribe to the dataset
    if (!dataRowId) {
      return of(context);
    }

    // If no page context - subscribe to the dataset directly
    if (!pageContext) {
      return this.observeDataRow(mainDatasetId, dataRowId, true);
    }

    // If we have a page context, start with it and observe the dataset
    return this.observeDataRow(mainDatasetId, dataRowId, false).pipe(startWith(context));
  }

  private getInitialData(namespace: string): InitialData {
    const runtimePages = syncSelect(this.store, runtimeSelectors.selectRuntimePages) ?? {};

    // Just isOnDialogLike is needed
    const rootElementId = getRootElementId(namespace);
    const rootElementType = syncSelect(this.store, getElementTypeById(rootElementId));
    const isOnDialogLike = rootElementType === 'dialog' || rootElementType === 'drawer';

    const currentPage = runtimePages[namespace];

    return { currentPage, isOnDialogLike };
  }

  private loadSampleData(source: DataBindingSourceInfo): Observable<DatasetRow | undefined> {
    let source$: Observable<any>;
    if (source.sourceType === 'dataset') {
      // Fetch one sample row
      source$ = this.datasetRowsService
        .get({ datasetId: source.id, maxResults: 1 })
        .pipe(catchError(() => of(undefined)));
    } else if (source.sourceType === 'context') {
      source$ = this.store.select(runtimeSelectors.selectWorkflowOutputsById(source.id));
    } else {
      source$ = of(undefined);
    }

    return source$.pipe(
      first(),
      map((data) => {
        if (!data) {
          return;
        }
        if (Array.isArray(data) && data.length) {
          data = data[0];
        }
        return data;
      }),
    );
  }

  private isInEditor(): boolean {
    return syncSelect(this.store, fromRouter.getIsInEditor) === true;
  }

  /**
   * Observe the data row for changes
   * @param mainDatasetId the dataset id to observe
   * @param dataRowId the id of the row to observe
   * @param startWithFetch if true, fetch the data immediately; false will wait for the dataset change before fetching
   * @returns an Observable with the latest row data
   */
  private observeDataRow(
    mainDatasetId: string,
    dataRowId: string,
    startWithFetch: boolean,
  ): Observable<{ dataRowId: string | null; data: DatasetRow[] }> {
    // Trigger dataset reload when dataset changes
    let trigger$: Observable<unknown> =
      this.datasetRowsService.observeDatasetAndRelatedChanged(mainDatasetId);

    // If startWithFetch is true, we need to trigger the fetch immediately
    if (startWithFetch) {
      trigger$ = trigger$.pipe(
        // Emit undefined to trigger at startup
        startWith(undefined),
      );
    }

    // On trigger, fetch the latest row
    return trigger$.pipe(
      switchMap(() => {
        // Fetch the schema to make sure the correct id column is used
        return this.schemaService.obtainSchema(mainDatasetId);
      }),
      switchMap((schema) => {
        // Fetch the latest row
        return this.datasetRowsService.getRowById(mainDatasetId, dataRowId, schema);
      }),
      map((data) => ({ dataRowId, data })),
    );
  }
}

function prepareDataForForm(
  ctx: PageContextWithSource,
  element: CanvasElement,
  emptyRow: DatasetRow,
): DatasetRow {
  let formOrigin: FormOrigin | undefined;

  if (isVariantOfFormContainer(element)) {
    formOrigin = element.props.content?.origin;
  }

  let data = {};
  if (formOrigin === 'generated' || ctx?.ctxSource !== 'pageContext') {
    data = Array.isArray(ctx.data) ? ctx.data[0] : ctx.data;
  }

  if (formOrigin === 'wizard' && (!data || isNullishOrEmptyObject(data))) {
    data = emptyRow;
  }

  return data;
}

/**
 * Update empty form with new empty row
 * For example, when the default values have changed
 * Non-empty fields will not be updated
 */
function updateFormIfEmptyRowChanged(
  element: CanvasElement,
  data: DatasetRow,
  previousEmptyRow: DatasetRow,
  emptyRow: DatasetRow,
  customFields: string[] | undefined,
): DatasetRow {
  if (!isEditableForm(element)) {
    return data;
  }

  return produce(data, (draft: DatasetRow) => {
    const isInsertForm = element.props.content.action === 'insert';
    for (const [key, defaultValue] of Object.entries(emptyRow)) {
      if (
        (!isNullish(defaultValue) || isInsertForm) &&
        (previousEmptyRow?.[key] === data[key] || data[key] === undefined) // prev value not changed or new value = undefined
      ) {
        draft[key] = defaultValue;
      }
    }

    if (customFields?.length) {
      fillCustomFields(draft, customFields);
    }
  });
}

function getFormCustomFieldsIfEditable(element: CanvasElement): string[] | undefined {
  if (!isEditableForm(element)) {
    return undefined;
  }

  return getFormCustomFields(element.props?.content?.model);
}
